import { useState, useEffect, useRef } from "react";
import { Landmark, Search, Loader2, Trash2, ExternalLink, RotateCcw, Wand2, AlertTriangle, RefreshCw, Sparkles, PlusCircle, HelpCircle, Star, ShieldCheck, GitBranch, ShieldAlert } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AutoResizeTextarea from "@/components/AutoResizeTextarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BACKEND_URL, uid, CAUTIONARY_TALES, REG_AGENCIES, REG_PATHWAYS, PRODUCT_TYPES } from "./constants";

// --- Inlined atoms ---
function FieldWand({ tab, field, fieldLabel, current, context, onAnswer, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/clinical/suggest-field`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab, field, fieldLabel, current, context }),
      });
      if (res.ok) { const d = await res.json(); if (d.answer) { onAnswer(d.answer); toast.success("AI draft ready"); } else toast.info("No draft returned"); }
      else toast.error("Failed to draft");
    } catch (e) { toast.error("Failed to draft"); }
    setBusy(false);
  };
  return (
    <Button size="sm" variant="ghost" onClick={run} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={testid} title="AI draft an answer">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
      <span className="ml-1 text-xs">AI draft</span>
    </Button>
  );
}
function WandField({ tab, field, label, value, onChange, onBlur, rows = 3, placeholder, context, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-slate-800">{label}</Label>
        <FieldWand tab={tab} field={field} fieldLabel={label} current={value} context={context} onAnswer={(a) => onChange(a)} testid={`${testid}-wand`} />
      </div>
      <AutoResizeTextarea value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} minRows={rows} maxHeight={560} className="mt-1 text-sm leading-relaxed" placeholder={placeholder} data-testid={testid} />
    </div>
  );
}
function CautionaryTale({ tale, testid }) {
  return (
    <Card className="border-amber-200 bg-amber-50 sticky top-4" data-testid={testid}>
      <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Cautionary tale · {tale.title}</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-xs text-amber-900">
        <p>{tale.body}</p>
        <p className="font-semibold">Lesson: <span className="font-normal">{tale.lesson}</span></p>
        {tale.extra && <p className="pt-2 border-t border-amber-200 italic">{tale.extra}</p>}
      </CardContent>
    </Card>
  );
}
function KeyQuestionsSection({ tab, tabData, questions, onSetQuestions, persistTab, testidBase }) {
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const suggest = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/clinical/suggest-key-questions`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab, tab_data: tabData, existing: questions.map((q) => q.text) }),
      });
      if (res.ok) {
        const d = await res.json();
        const next = [...questions, ...(d.questions || []).map((t) => ({ id: uid(), text: t }))];
        onSetQuestions(next); persistTab(next);
        if (d.questions?.length) toast.success(`Added ${d.questions.length} AI questions`);
        else toast.info("No suggestions returned");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  const add = () => { if (!input.trim()) return; const next = [...questions, { id: uid(), text: input.trim() }]; onSetQuestions(next); persistTab(next); setInput(""); };
  const remove = (id) => { const next = questions.filter((q) => q.id !== id); onSetQuestions(next); persistTab(next); };
  return (
    <Card data-testid={`${testidBase}-section`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><HelpCircle className="w-4 h-4 text-teal-600" /> Key questions</CardTitle>
          <Button size="sm" variant="ghost" onClick={suggest} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={`${testidBase}-suggest`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">AI suggest</span>
          </Button>
        </div>
        <p className="text-xs text-slate-500">Sharp open questions you should be able to answer to defend this section.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {questions.length === 0 && <p className="text-xs text-slate-400 italic">No questions yet. Add your own or click AI suggest.</p>}
        {questions.map((q) => (
          <div key={q.id} className="flex items-start gap-2 border-l-2 border-l-teal-300 pl-3 py-1.5" data-testid={`${testidBase}-q-${q.id}`}>
            <span className="text-sm text-slate-700 flex-1 leading-snug">{q.text}</span>
            <button onClick={() => remove(q.id)} className="text-slate-300 hover:text-red-500 flex-shrink-0" data-testid={`${testidBase}-q-del-${q.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex gap-2 pt-2 border-t">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add your own question…" className="text-sm h-9" data-testid={`${testidBase}-input`} />
          <Button size="sm" onClick={add} data-testid={`${testidBase}-add`}><PlusCircle className="w-4 h-4" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

const OUTCOME_COLORS = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  crl: "bg-red-50 text-red-700 border-red-200",
  withdrawn: "bg-slate-100 text-slate-600 border-slate-200",
  conditional: "bg-amber-50 text-amber-700 border-amber-200",
};
const outcomeBadge = (outcome) => {
  const key = (outcome || "").toLowerCase().split(" ")[0].split("/")[0];
  return OUTCOME_COLORS[key] || "bg-slate-100 text-slate-600 border-slate-200";
};

export default function RegulatoryRouteTab({ data, setData, persist, companyProfile }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const set = (patch) => setData({ ...data, ...patch });
  const setField = (k) => (v) => set({ [k]: v });
  const persistTab = (nextRr) => persist({ regulatory_route: nextRr });

  const runSearch = async (searchQuery, replace = false) => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/clinical/search-decisions`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ query: searchQuery, context: { product_type: data.product_type, pathway_strategy: data.pathway_strategy, primary_agency: data.primary_agency, key_risks: data.key_risks } }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.not_configured) { toast.error("Tavily API key not set"); }
        else if (d.decisions?.length) {
          const nextList = replace ? d.decisions : [...(data.precedent_decisions || []), ...d.decisions];
          const nextRr = { ...data, precedent_decisions: nextList };
          setData(nextRr); persistTab(nextRr);
          toast.success(d.auto_curated ? `Auto-curated ${d.decisions.length} precedent decisions` : `Added ${d.decisions.length} decisions`);
        } else toast.info(d.message || "No precedent decisions found");
      } else toast.error("Search failed");
    } catch (e) { toast.error("Search failed"); }
    setBusy(false);
  };

  const autoCurated = useRef(false);
  useEffect(() => {
    if (autoCurated.current) return;
    if (!companyProfile && !data.product_type) return;
    if ((data.precedent_decisions || []).length > 0) return;
    autoCurated.current = true;
    setAutoLoading(true);
    runSearch("", true).finally(() => setAutoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyProfile]);

  const removeDec = (id) => {
    const nextRr = { ...data, precedent_decisions: (data.precedent_decisions || []).filter((d) => d.id !== id) };
    setData(nextRr); persistTab(nextRr);
  };
  const resetTab = () => {
    if (window.confirm("Reset the Regulatory Route tab? This clears all answers and precedents.")) {
      const empty = { product_type: "", designations_eligible: "", designation_status: "", primary_agency: "", pathway_strategy: "", agency_interactions_planned: "", cmc_readiness: "", key_risks: "", mitigations: "", key_questions: [], precedent_decisions: [] };
      setData(empty); persistTab(empty); toast.success("Regulatory Route reset");
    }
  };
  const refresh = () => runSearch("", true);

  const ctx = { product_type: data.product_type, primary_agency: data.primary_agency, pathway_strategy: data.pathway_strategy, designations_eligible: data.designations_eligible, key_risks: data.key_risks };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="regulatory-route-tab">
      <div className="lg:col-span-2 space-y-4">
        {/* A. Product Classification */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal-600" /> A · Product classification</CardTitle>
              <Button size="sm" variant="ghost" onClick={resetTab} className="h-7 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="rr-reset-btn"><RotateCcw className="w-3.5 h-3.5" /></Button>
            </div>
            <p className="text-xs text-slate-500">Regulatory classification drives everything downstream — pathway, agency, evidence bar, timelines.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-slate-800">Product type</Label>
              <Select value={data.product_type || ""} onValueChange={(v) => { const next = { ...data, product_type: v }; setData(next); persistTab(next); }}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="rr-product-type"><SelectValue placeholder="Select product type…" /></SelectTrigger>
                <SelectContent>{PRODUCT_TYPES.map((p) => <SelectItem key={p} value={p} className="text-sm">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <WandField tab="regulatory_route" field="designations_eligible" label="Special designations you could pursue" value={data.designations_eligible} onChange={setField("designations_eligible")} onBlur={persist} rows={3} placeholder="Orphan (FDA/EMA), Fast Track, Breakthrough, RMAT, PRIME, ILAP, Paediatric Investigation Plan…" context={ctx} testid="rr-designations" />
            <WandField tab="regulatory_route" field="designation_status" label="Designation status" value={data.designation_status} onChange={setField("designation_status")} onBlur={persist} rows={2} placeholder="Planned / applied / secured — and by when." context={ctx} testid="rr-desig-status" />
          </CardContent>
        </Card>

        {/* B. Approval pathway */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><GitBranch className="w-4 h-4 text-teal-600" /> B · Approval pathway</CardTitle>
            <p className="text-xs text-slate-500">Which agency, which pathway, what interactions you plan to have and by when.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium text-slate-800">Primary agency</Label>
                <Select value={data.primary_agency || ""} onValueChange={(v) => { const next = { ...data, primary_agency: v }; setData(next); persistTab(next); }}>
                  <SelectTrigger className="mt-1 h-9 text-sm" data-testid="rr-agency"><SelectValue placeholder="Select agency…" /></SelectTrigger>
                  <SelectContent>{REG_AGENCIES.map((a) => <SelectItem key={a} value={a} className="text-sm">{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-800">Pathway strategy</Label>
                <Select value={data.pathway_strategy || ""} onValueChange={(v) => { const next = { ...data, pathway_strategy: v }; setData(next); persistTab(next); }}>
                  <SelectTrigger className="mt-1 h-9 text-sm" data-testid="rr-pathway"><SelectValue placeholder="Select pathway…" /></SelectTrigger>
                  <SelectContent>{REG_PATHWAYS.map((p) => <SelectItem key={p} value={p} className="text-sm">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <WandField tab="regulatory_route" field="agency_interactions_planned" label="Agency interactions planned" value={data.agency_interactions_planned} onChange={setField("agency_interactions_planned")} onBlur={persist} rows={3} placeholder="Pre-IND meeting Q3 · EMA scientific advice Q1 · MHRA ILAP innovation passport · End-of-Phase-2 meeting…" context={ctx} testid="rr-interactions" />
            <WandField tab="regulatory_route" field="cmc_readiness" label="CMC / manufacturing readiness" value={data.cmc_readiness} onChange={setField("cmc_readiness")} onBlur={persist} placeholder="Where you are on process, comparability, GMP, potency assay — and what's on the critical path for pivotal." context={ctx} testid="rr-cmc" />
          </CardContent>
        </Card>

        {/* C. Regulatory risks & precedents */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-teal-600" /> C · Regulatory risks & precedents</CardTitle>
            <p className="text-xs text-slate-500">What could block approval — and what the last 5 years of decisions in this space are telling you.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <WandField tab="regulatory_route" field="key_risks" label="Key regulatory risks" value={data.key_risks} onChange={setField("key_risks")} onBlur={persist} rows={4} placeholder="Endpoint dispute, small N, single-arm, CMC comparability, safety signal at high dose, adcom split…" context={ctx} testid="rr-risks" />
            <WandField tab="regulatory_route" field="mitigations" label="Mitigation plan" value={data.mitigations} onChange={setField("mitigations")} onBlur={persist} rows={3} placeholder="How you'll de-risk each above — natural history control, pre-specified sensitivity analyses, EMA scientific advice, etc." context={ctx} testid="rr-mitigations" />
          </CardContent>
        </Card>

        {/* Auto-curated precedent decisions */}
        <Card data-testid="rr-decisions-section">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Sparkles className="w-4 h-4 text-teal-600" /> Precedent regulatory decisions <Badge variant="outline" className="text-[10px] ml-1">Auto-curated</Badge></CardTitle>
              <Button size="sm" variant="ghost" onClick={refresh} disabled={busy} className="h-7 px-2 text-teal-600 hover:bg-teal-50" data-testid="rr-refresh-btn" title="Refresh from FDA / EMA / MHRA sources">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">Refresh suggestions</span>
              </Button>
            </div>
            <p className="text-xs text-slate-500">FDA / EMA / MHRA approvals AND rejections in your class + indication — with the agency reasoning.</p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3">
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch(q, false)} placeholder="Or search a specific term (e.g. AAV gene therapy accelerated approval)" className="text-sm" data-testid="rr-search-input" />
              <Button size="sm" onClick={() => runSearch(q, false)} disabled={busy} data-testid="rr-search-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
            </div>
            <div className="space-y-2">
              {(data.precedent_decisions || []).length === 0 && !busy && <p className="text-xs text-slate-400 italic">Fill in product classification to auto-curate precedent decisions.</p>}
              {busy && (data.precedent_decisions || []).length === 0 && <p className="text-xs text-teal-600 italic flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Auto-curating precedent decisions…</p>}
              {(data.precedent_decisions || []).map((d) => (
                <div key={d.id} className="border rounded-lg p-3 bg-white" data-testid={`rr-dec-${d.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1">
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-slate-800 hover:text-teal-700 leading-snug">
                        {d.product_name || "Unnamed"}{d.sponsor && <span className="text-slate-400 font-normal"> · {d.sponsor}</span>} {d.url && <ExternalLink className="w-3 h-3 inline text-slate-400" />}
                      </a>
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {d.agency && <Badge variant="outline" className="text-[10px]">{d.agency}</Badge>}
                        {d.pathway && <Badge variant="outline" className="text-[10px]">{d.pathway}</Badge>}
                        {d.outcome && <Badge className={`text-[10px] border ${outcomeBadge(d.outcome)}`} variant="outline">{d.outcome}</Badge>}
                        {d.year && <Badge variant="outline" className="text-[10px]">{d.year}</Badge>}
                      </div>
                    </div>
                    {typeof d.relevance_score === "number" && d.relevance_score > 0 && <Badge variant="secondary" className="text-[10px] flex items-center gap-1"><Star className="w-2.5 h-2.5" />{d.relevance_score}</Badge>}
                    <button onClick={() => removeDec(d.id)} className="text-slate-300 hover:text-red-500" data-testid={`rr-dec-del-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {d.indication && <p className="text-[11px] text-slate-500 mt-1">Indication: {d.indication}</p>}
                  {d.key_reasoning && <p className="text-[11px] text-slate-700 mt-1"><span className="text-slate-400">Reasoning:</span> {d.key_reasoning}</p>}
                  {d.why_relevant && <p className="text-[11px] text-teal-700 italic mt-1">Why relevant: {d.why_relevant}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="lg:col-span-1"><CautionaryTale tale={CAUTIONARY_TALES.regulatory_route} testid="rr-cautionary" /></div>
    </div>
  );
}
